import { useMutation, useQuery } from '@apollo/client/react';
import { faPaperPlaneAlt, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  App,
  ChatDocument,
  ChatRoomKind,
  CreateChatDocument,
  SelfDocument,
} from '../../graphql/types';
import { useMemberSearch } from '../../hooks/useMemberSearch';
import { WHITE } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventProfileCardNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Button, { ButtonVariant } from '../reusable/Button';
import { Divider } from '../reusable/Divider';
import { MemberAutocomplete } from '../reusable/MemberAutocomplete';
import { SnackbarVariant } from '../reusable/Snackbar';
import { useSnackbarContext } from '../reusable/Snackbar/SnackbarContextProvider';
import TextAreaInput from '../reusable/TextAreaInput';
import { ChatSize, useChatContext } from './Context';
import { Header } from './Header';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledFormContent = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 8px;
  gap: 8px;
  height: 100%;
`;

const StyledFormFooter = styled.div`
  display: flex;
  justify-content: end;
  padding: 8px;
`;

interface CreateChatValues {
  memberId: string;
  initialMessage: string;
}

interface CreateChatProps {
  closeable?: boolean;
}

export const CreateChat: React.FC<CreateChatProps> = ({ closeable }) => {
  const methods = useForm<CreateChatValues>();
  const { handleSubmit, watch, formState, setValue } = methods;
  const {
    openChat,
    chatSize,
    closeChat,
    member: passedInMember,
  } = useChatContext();
  const { setSnackbar } = useSnackbarContext();

  const [createChat] = useMutation(CreateChatDocument);

  const memberIdWatcher = watch('memberId');

  const { data: selfData } = useQuery(SelfDocument);
  const selfId = selfData?.self?.id;
  const {
    members: usersList,
    loading: userListLoading,
    search: handleAutocompleteInput,
  } = useMemberSearch({
    skip: chatSize === ChatSize.Closed,
    hasSignedIn: true,
  });

  // The selected member drives the chat lookup declaratively — passedInMember
  // takes precedence (and applies immediately, before the setValue effect runs).
  const selectedMemberId = passedInMember?.id ?? memberIdWatcher;
  const { data: chatData, loading: isLoadingSelecterMemberChat } = useQuery(
    ChatDocument,
    {
      variables: { userIds: [selectedMemberId ?? '', selfId ?? ''] },
      skip: !selectedMemberId || !selfId,
    },
  );

  const handleFormSubmit = async (values: CreateChatValues) => {
    const { memberId, initialMessage } = values;
    if (!selfId) {
      return;
    }
    const createdChat = await createChat({
      variables: {
        input: {
          name: `Coach created: ${memberId}`,
          users: [selfId, memberId],
          app: App.Heart,
          kind: ChatRoomKind.Direct,
          initialMessage,
        },
      },
      refetchQueries: ['chats'],
      onError: () =>
        setSnackbar({
          icon: faWarning,
          message: 'An error occured while creating the chat.',
          variant: SnackbarVariant.Danger,
        }),
    });

    if (!createdChat.error) {
      closeChat();
      const chatId = createdChat.data?.createChatRoom.chatRoom?.id;
      const chatMember = usersList?.find((user) => user.id === memberId);
      if (chatId) {
        openChat({
          chatId,
          member: chatMember,
        });
      }
    }
  };

  useEffect(() => {
    if (chatSize === ChatSize.Closed) {
      methods.reset();
    }
  }, [chatSize, methods]);

  useEffect(() => {
    if (passedInMember?.id) {
      setValue('memberId', passedInMember.id);
    }
  }, [passedInMember?.id, setValue]);

  // When the selected member already has a chat, open it. Re-running on later
  // usersList resolution is harmless (and refines the member info passed along).
  const selectedChat = chatData?.chatRoom;
  useEffect(() => {
    if (selectedChat) {
      openChat({
        chatId: selectedChat.id,
        member:
          passedInMember ??
          usersList?.find((user) => user.id === selectedMemberId),
        updateChatSize: false,
      });
    }
  }, [selectedChat, openChat, passedInMember, usersList, selectedMemberId]);

  return (
    <>
      <Header
        content={{ title: 'New message', hideEmptyElements: true }}
        closeable={closeable}
      />
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid="createChat-form"
        >
          <StyledFormContent>
            <Controller
              name="memberId"
              rules={{
                required: 'Member is required',
              }}
              render={({ field }) => (
                <MemberAutocomplete
                  disabled={
                    Boolean(passedInMember) || isLoadingSelecterMemberChat
                  }
                  name={field.name}
                  selectedUserId={passedInMember?.id ?? field.value}
                  users={usersList || []}
                  onInputChange={handleAutocompleteInput}
                  onSelection={(user) => {
                    // Setting the form value drives the chat lookup query.
                    field.onChange(user?.id || '');
                  }}
                  placeholder="Search for a member"
                  loading={userListLoading}
                  trackingData={{
                    event_category: EventCategories.Chat,
                    card_name: EventProfileCardNames.CreateChat,
                  }}
                />
              )}
            />
            {isLoadingSelecterMemberChat && (
              <LoadingPanel noBorder fullHeight />
            )}
            {memberIdWatcher && !isLoadingSelecterMemberChat && (
              <TextAreaInput
                formRegister={methods.register('initialMessage', {
                  required: 'Message is required',
                })}
                placeholder="Write a message"
                backgroundColor={WHITE}
                height="100%"
                trackingData={{
                  event_category: EventCategories.Chat,
                  card_name: EventProfileCardNames.CreateChat,
                  event_label: EventLabels.InitialMessage,
                }}
              />
            )}
          </StyledFormContent>

          <Divider $marginBottom={0} $marginTop={0} />

          <StyledFormFooter>
            <Button
              type="submit"
              variant={ButtonVariant.Dark}
              icon={<FontAwesomeIcon icon={faPaperPlaneAlt} />}
              trackingData={{
                event_category: EventCategories.Chat,
                card_name: EventProfileCardNames.CreateChat,
                event_label: EventLabels.Create,
              }}
              disabled={formState.isSubmitting}
              loading={formState.isSubmitting}
            >
              Send
            </Button>
          </StyledFormFooter>
        </StyledForm>
      </FormProvider>
    </>
  );
};
